import { useMatches, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth, useGuard } from '../../hooks/auth'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'
import {
  Container,
  RegistrationBox,
  OwnerBox,
  StyledForm,
  BankBox,
  AgencyBox,
  AccountIdentifierBox,
  AccountNumberBox,
  FormDivider,
  PageSubtitle,
  PageTitle,
  PageHeaderBox,
  TextHeaderBox,
  ButtonHeaderBox,
  DigitBox,
  ExceptionContainer,
  GridForm,
  AccountTypeBox,
  CancelButtonBox,
  SaveButtonBox,
  HorizontalContainer,
  TransactionTypeBox,
  PixTypeKeyBox,
  PixKeyBox,
  ButtonsContainer,
} from './styles'
import { DashboardhMediumButton } from 'bd-web-components'
// @ts-ignore
import { FormikState, useFormik } from 'formik'
import { createBankAccount } from '../../Service/accounts'
// @ts-ignore
import { toast } from 'react-toastify'
import { ReactElement, useEffect, useState, useMemo } from 'react'
import {
  accountBaseData,
  transactionTypeOptions as defaultTransactionTypeOptions,
} from '../../utils/bank-account'
import { isLetter } from '../../utils/string-modeling'
import { addEditAccountValidation } from '../../utils/yup-validation'
import { IInitalAddValues } from '../../types/bank-account'
import Loader from '../../components/Loader'
import FormikField from '../../components/Field'
import { getDashColor } from '../../styles'
import { bankItemProps, useBankList } from '../../hooks/bank'
import { cpfCNPJSanitazer } from '../../utils/withdraw-helpers'
import { pixKeyOptions } from '../../utils/pix-account'
import { Breadcrumb } from '../../components/Breadcrumb'

export function AddAccount(): ReactElement {
  const authContext = useAuth()
  const navigate = useNavigate()
  const { cookies, authHookWithOutEvent } = authContext
  const authToken = LOCAL_EXECUTION
    ? process.env.REACT_APP_LOCAL_EXECUTION_TOKEN
    : cookies.userJWT

  // verify if user has this event
  if (!LOCAL_EXECUTION) {
    authHookWithOutEvent(authToken as string)
  }

  // verify if user has permission to acess
  useGuard(['DP5'])

  // ---------------------------------------------------------
  const [bankList, setBankList] = useState<bankItemProps[]>([])
  const useBankListObject = useBankList()
  const [loading, setLoading] = useState<boolean>(false)
  const [usePixForm, setUsePixForm] = useState<boolean>(false)
  const [searchParams] = useSearchParams()
  const transactionTypeFromURL = searchParams.get('transactionType')

  const transactionTypeDefaultValue = useMemo(() => {
    return (
      defaultTransactionTypeOptions.find(
        (option) => option.value === transactionTypeFromURL
      ) || {
        id: 0,
        value: '',
        label: 'Selecione o tipo de transação',
      }
    )
  }, [transactionTypeFromURL])

  let matches = useMatches()
  let crumbs = matches
    .filter((match: any) => Boolean(match.handle?.crumb))
    .map((match: any) => {
      return {
        name: match.handle.crumb(match.data),
        path: match.pathname,
      }
    })

  const initial: IInitalAddValues = {
    nationalRegistration: '',
    accountOwner: '',
    bank: '',
    bankName: '',
    agency: '',
    accountType: '',
    accountNumber: '',
    accountDigit: '',
    accountIdentifier: '',
    transactionType: transactionTypeFromURL || '',
    pixKey: '',
    pixTypeKey: '',
  }

  const useFormikProps = useFormik({
    enableReinitialize: true,
    initialValues: initial,
    validationSchema: addEditAccountValidation,
    onSubmit: async (
      values: IInitalAddValues,
      { setSubmitting, resetForm }: any
    ) => {
      values.accountDigit = isLetter(values.accountDigit)
      values.nationalRegistration = cpfCNPJSanitazer(
        values.nationalRegistration.toString()
      )
      setLoading(true)
      const createAccountRequest = await createBankAccount(authToken, values)
      if (createAccountRequest) {
        toast.success('Conta cadastrada com sucesso!')
        resetForm(initial as Partial<FormikState<IInitalAddValues>>)
        setLoading(false)
      }
      if (!createAccountRequest)
        toast.error('Um erro aconteceu, tente novamente')
      setSubmitting(false)
      setLoading(false)
    },
  })

  const handlerNavigate = (url: string) => {
    navigate(url)
  }

  const bankListWithNumberAndName = bankList.map((bank: any) => {
    return {
      id: bank.id,
      value: bank.value,
      label: `${bank.value} - ${bank.label}`,
    }
  })

  useEffect(() => {
    if (useBankListObject.bankList !== undefined) {
      const data = useBankListObject.bankList.data.data
      setBankList(data)
    }
  }, [useBankListObject])

  useEffect(() => {
    if (useFormikProps.values.transactionType === 'PIX') {
      setUsePixForm(true)
    } else {
      setUsePixForm(false)
    }
  }, [useFormikProps.values.transactionType])

  return (
    <>
      <Container>
        <Breadcrumb paths={crumbs} />
        <PageHeaderBox>
          <TextHeaderBox>
            <PageTitle>{'Adicionar Nova Conta'}</PageTitle>
            <PageSubtitle>
              {
                'Esta página permite a adição de uma nova conta bancária para facilitar suas futuras transferências.'
              }
            </PageSubtitle>
          </TextHeaderBox>
          <ButtonHeaderBox>
            <DashboardhMediumButton
              onClick={() => handlerNavigate('/finance/bank-accounts')}
              color="#fff"
              background={getDashColor('SECONDARY_BLUE_01', 'GRAY_DARK')}
              height="48px"
              maxWidth="100%"
              variant="contained"
              width="204px"
              borderRadius='100px'
            >
              {'Contas Bancárias'}
            </DashboardhMediumButton>
          </ButtonHeaderBox>
        </PageHeaderBox>
        <FormDivider />
        {/* LOADING CASE */}
        {bankList[0] === undefined && (
          <ExceptionContainer height={400} margin={false}>
            <Loader />
          </ExceptionContainer>
        )}
        {bankList[0] !== undefined && (
          <StyledForm onSubmit={useFormikProps.handleSubmit}>
            <GridForm>
              <HorizontalContainer>
                <RegistrationBox>
                  <FormikField
                    formikFieldType={'input'}
                    inputType={'text'}
                    name={'nationalRegistration'}
                    useFormik={useFormikProps}
                    title={'CPF/CNPJ'}
                    inputPlaceholder={'CPF ou CNPJ'}
                    maxLength={20}
                  />
                </RegistrationBox>
                <OwnerBox>
                  <FormikField
                    formikFieldType={'input'}
                    inputType={'text'}
                    name={'accountOwner'}
                    useFormik={useFormikProps}
                    title={'Titular/Razão Social'}
                    inputPlaceholder={'Titular da conta'}
                    maxLength={100}
                  />
                </OwnerBox>
              </HorizontalContainer>

              <TransactionTypeBox>
                <FormikField
                  formikFieldType={'dropdown'}
                  defaultValue={transactionTypeDefaultValue}
                  dropdownOptions={defaultTransactionTypeOptions}
                  name={'transactionType'}
                  useFormik={useFormikProps}
                  title={'Tipo de transação'}
                />
              </TransactionTypeBox>

              {!usePixForm && (
                <>
                  <BankBox>
                    <FormikField
                      formikFieldType={'dropdown'}
                      defaultValue={{
                        id: 0,
                        value: '0',
                        label: 'Selecione o Banco',
                      }}
                      dropdownOptions={bankListWithNumberAndName}
                      name={'bank'}
                      useFormik={useFormikProps}
                      title={'Banco'}
                    />
                  </BankBox>
                  <HorizontalContainer>
                    <AgencyBox>
                      <FormikField
                        formikFieldType={'input'}
                        inputType={'text'}
                        name={'agency'}
                        useFormik={useFormikProps}
                        title={'Agência'}
                        inputPlaceholder={'0000'}
                        maxLength={4}
                      />
                    </AgencyBox>
                    <AccountTypeBox>
                      <FormikField
                        formikFieldType={'dropdown'}
                        defaultValue={{
                          id: 0,
                          value: '0',
                          label: 'Selecione o tipo de conta',
                        }}
                        dropdownOptions={accountBaseData}
                        name={'accountType'}
                        useFormik={useFormikProps}
                        title={'Tipo de conta'}
                      />
                    </AccountTypeBox>
                  </HorizontalContainer>
                  <HorizontalContainer>
                    <AccountNumberBox>
                      <FormikField
                        formikFieldType={'input'}
                        inputType={'text'}
                        name={'accountNumber'}
                        useFormik={useFormikProps}
                        title={'Número da Conta'}
                        inputPlaceholder={'Número da conta'}
                        maxLength={20}
                      />
                    </AccountNumberBox>
                    <DigitBox>
                      <FormikField
                        formikFieldType={'input'}
                        inputType={'text'}
                        name={'accountDigit'}
                        useFormik={useFormikProps}
                        title={'Dígito'}
                        inputPlaceholder={'Digito da conta'}
                        maxLength={1}
                      />
                    </DigitBox>
                  </HorizontalContainer>
                </>
              )}

              {usePixForm && (
                <HorizontalContainer>
                  <PixTypeKeyBox>
                    <FormikField
                      formikFieldType={'dropdown'}
                      defaultValue={{
                        id: 0,
                        value: '0',
                        label: 'Selecione o tipo da chave',
                      }}
                      dropdownOptions={pixKeyOptions}
                      name={'pixTypeKey'}
                      useFormik={useFormikProps}
                      title={'Tipo de chave PIX'}
                    />
                  </PixTypeKeyBox>
                  <PixKeyBox>
                    <FormikField
                      formikFieldType={'input'}
                      inputType={'text'}
                      name={'pixKey'}
                      useFormik={useFormikProps}
                      title={'Chave PIX'}
                      inputPlaceholder={'Digite sua chave PIX'}
                      maxLength={100}
                    />
                  </PixKeyBox>
                </HorizontalContainer>
              )}

              <AccountIdentifierBox>
                <FormikField
                  formikFieldType={'input'}
                  inputType={'text'}
                  name={'accountIdentifier'}
                  useFormik={useFormikProps}
                  title={'Nome da conta'}
                  inputPlaceholder={'Escreva aqui o nome da conta'}
                  maxLength={100}
                />
              </AccountIdentifierBox>

              <ButtonsContainer>
                <SaveButtonBox>
                  <DashboardhMediumButton
                    //@ts-ignore
                    type="submit"
                    //@ts-ignore
                    disable={
                      loading ||
                      (useFormikProps.dirty && !useFormikProps.isValid)
                    }
                    background={getDashColor('PRIMARY_BLUE_02', 'SECONDARY_COLOR')}
                    color=""
                    height="52px"
                    maxWidth="100%"
                    variant={
                      loading ||
                        (useFormikProps.dirty && !useFormikProps.isValid)
                        ? 'disabled'
                        : 'contained'
                    }
                    borderRadius='100px'
                  >
                    {!loading && <>{'Salvar Conta'}</>}
                    {loading && <Loader small={true} />}
                  </DashboardhMediumButton>
                </SaveButtonBox>
                <CancelButtonBox>
                  <DashboardhMediumButton
                    onClick={() => handlerNavigate('/finance/bank-accounts')}
                    background=""
                    color=""
                    height="52px"
                    maxWidth="100%"
                    variant={'outlined'}
                    borderRadius='100px'
                  >
                    {'Cancelar'}
                  </DashboardhMediumButton>
                </CancelButtonBox>
              </ButtonsContainer>
            </GridForm>
          </StyledForm>
        )}
      </Container>
    </>
  )
}
